import type { CategoryId } from "../../../features/catalog/model";
import { pctFine } from "../../../lib/format";
import { m } from "../../../paraglide/messages";
import { BandLogo } from "./BandLogo";
import { fmtTraffic, labelFor, type DayReading, type TrafficUnit } from "./dailySeries";

export interface DayTooltipProps {
  reading: DayReading;
  /** what the day's numbers are counted in */
  unit: TrafficUnit;
  /** the colour each band is drawn in, so the card and the plot agree */
  hues: Record<string, string>;
  /** the hovered column's centre, as a percentage of the plot width */
  atPct: number;
  /** catalog category per catalog card id, for the platform marks */
  categories: Record<string, CategoryId>;
}

/** A platform's share of the day. Rounding a real slice to `0%` reads as an
 *  error, so anything under half a percent says so instead. */
const share = (value: number, total: number): string => {
  const pct = (value / total) * 100;
  return pct < 0.5 ? "<1%" : `${Math.round(pct)}%`;
};

/** Weekday and date, in the UTC day the bucket is keyed by. */
const stamp = (iso: string): string =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });

/** Tracked uppercase mono, the way the page labels every figure. */
const LABEL = "mono text-10 uppercase leading-none tracking-widest text-text-3";

/**
 * What one day was made of: two readings in the page's own tape style, the
 * day's total under its date and the day's uptime across every platform, then
 * every platform that served traffic that day, biggest first, each with its
 * logo, its own share and a bar in its band's colour. A day with no measured
 * request shows a dash for uptime, never a figure. It is the reason to hover at
 * all, so it carries the whole split rather than a single number.
 *
 * The readings sit on one line, each label over its figure, so the card with
 * every band in it still ends inside the plot rather than over the legend.
 *
 * It is anchored to the hovered column and flips its own alignment near either
 * edge, so the card never leaves the plot.
 */
export const DayTooltip = ({ reading, unit, hues, atPct, categories }: DayTooltipProps) => {
  const nearRight = atPct > 65;
  const nearLeft = atPct < 35;
  const align = nearRight
    ? "translate-x-[-100%]"
    : nearLeft
      ? ""
      : "translate-x-[-50%]";
  const rows = [...reading.parts].sort((a, b) => b.value - a.value);
  // Bars are sized against the day's biggest platform, so the top one is full
  // width and the rest step down the card.
  const biggest = rows[0]?.value || 1;
  return (
    <div
      className={`pointer-events-none absolute top-0 z-20 ${align}`}
      style={{ left: `${atPct}%` }}
    >
      <div className="w-max min-w-45 rounded-10 border border-rule bg-surface p-3 shadow-lg">
        <dl className="grid grid-cols-2 divide-x divide-rule">
          <div className="pr-3">
            <dt className={LABEL}>{stamp(reading.date)}</dt>
            <dd className="mono mt-1.5 text-16 leading-none text-text">
              {fmtTraffic(unit, reading.total)}
            </dd>
          </div>
          <div className="pl-3">
            <dt className={LABEL}>{m.measure_uptime()}</dt>
            <dd className="mono mt-1.5 text-16 leading-none text-text">
              {reading.uptimePct === undefined ? "-" : pctFine(reading.uptimePct)}
            </dd>
          </div>
        </dl>
        <div className="mt-2.5 flex flex-col gap-2 border-t border-line-2 pt-2.5">
          {rows.map((part) => (
            <div key={part.id} className="flex items-start gap-2.5">
              <span className="flex w-3.5 shrink-0 justify-center pt-px">
                <BandLogo id={part.id} categories={categories} size={14} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2.5">
                  <span className="flex-1 whitespace-nowrap text-11 text-text-2">
                    {labelFor(part.id)}
                  </span>
                  <span className="mono text-11 text-text">
                    {fmtTraffic(unit, part.value)}
                  </span>
                  <span className="mono w-9 text-right text-10 text-text-3">
                    {share(part.value, reading.total)}
                  </span>
                </div>
                <span
                  className="mt-1 block h-1 rounded-2"
                  style={{
                    width: `${(part.value / biggest) * 100}%`,
                    background: hues[part.id] ?? "var(--c-slate)",
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
